/**
 * Image Upload Service
 * Handles immediate image uploads to temporary server storage
 * with retry logic, progress tracking, and queue management
 */

export interface TempImageUploadResponse {
  uuid: string;
  url: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadQueueItem {
  id: string;
  file: File;
  retryCount: number;
  maxRetries: number;
  status: "pending" | "uploading" | "success" | "error";
  progress?: UploadProgress;
  result?: TempImageUploadResponse;
  error?: string;
  abortController?: AbortController;
  onProgress?: (progress: UploadProgress) => void;
  onSuccess?: (result: TempImageUploadResponse) => void;
  onError?: (error: string) => void;
}

export class ImageUploadService {
  private uploadQueue: Map<string, UploadQueueItem> = new Map();
  private activeUploads: Set<string> = new Set();
  private maxConcurrentUploads = 3;
  private baseUrl = "/api/upload/temp";
  private queueEndpoint = "/api/upload/queue-processing";

  /**
   * Add image to upload queue and start upload immediately
   * @param file Image file to upload
   * @param options Upload options
   * @returns Upload queue item ID
   */
  async uploadImage(
    file: File,
    options: {
      onProgress?: (progress: UploadProgress) => void;
      onSuccess?: (result: TempImageUploadResponse) => void;
      onError?: (error: string) => void;
      maxRetries?: number;
    } = {},
  ): Promise<string> {
    const uploadId = this.generateUploadId();
    const { maxRetries = 3, onProgress, onSuccess, onError } = options;

    // Validate file
    if (!this.isValidImageFile(file)) {
      const error = "Invalid file type";
      onError?.(error);
      throw new Error(error);
    }

    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit
      const error = "File size exceeds 10MB limit";
      onError?.(error);
      throw new Error(error);
    }

    // Create queue item
    const queueItem: UploadQueueItem = {
      id: uploadId,
      file,
      retryCount: 0,
      maxRetries,
      status: "pending",
      onProgress,
      onSuccess,
      onError,
    };

    this.uploadQueue.set(uploadId, queueItem);

    // Start upload immediately
    this.processUploadQueue();

    return uploadId;
  }

  /**
   * Cancel upload by ID
   * @param uploadId Upload ID to cancel
   */
  cancelUpload(uploadId: string): void {
    const queueItem = this.uploadQueue.get(uploadId);
    if (queueItem) {
      // Abort if currently uploading
      if (queueItem.abortController) {
        queueItem.abortController.abort();
      }

      // Remove from queue and active uploads
      this.uploadQueue.delete(uploadId);
      this.activeUploads.delete(uploadId);
    }
  }

  /**
   * Get upload status by ID
   * @param uploadId Upload ID
   * @returns Upload queue item or null
   */
  getUploadStatus(uploadId: string): UploadQueueItem | null {
    return this.uploadQueue.get(uploadId) || null;
  }

  /**
   * Retry failed upload
   * @param uploadId Upload ID to retry
   */
  retryUpload(uploadId: string): void {
    const queueItem = this.uploadQueue.get(uploadId);
    if (queueItem && queueItem.status === "error") {
      queueItem.status = "pending";
      queueItem.retryCount = 0;
      queueItem.error = undefined;
      this.processUploadQueue();
    }
  }

  /**
   * Get all uploads with their statuses
   * @returns Array of upload queue items
   */
  getAllUploads(): UploadQueueItem[] {
    return Array.from(this.uploadQueue.values());
  }

  /**
   * Clear completed uploads from queue
   */
  clearCompletedUploads(): void {
    for (const [id, item] of this.uploadQueue.entries()) {
      if (item.status === "success") {
        this.uploadQueue.delete(id);
      }
    }
  }

  /**
   * Process upload queue - start uploads up to concurrent limit
   */
  private async processUploadQueue(): Promise<void> {
    // Find pending uploads
    const pendingUploads = Array.from(this.uploadQueue.values())
      .filter((item) => item.status === "pending")
      .slice(0, this.maxConcurrentUploads - this.activeUploads.size);

    // Start uploads
    for (const queueItem of pendingUploads) {
      if (this.activeUploads.size >= this.maxConcurrentUploads) {
        break;
      }

      this.startUpload(queueItem);
    }
  }

  /**
   * Start individual upload with retry logic
   * @param queueItem Upload queue item
   */
  private async startUpload(queueItem: UploadQueueItem): Promise<void> {
    this.activeUploads.add(queueItem.id);
    queueItem.status = "uploading";
    queueItem.abortController = new AbortController();

    try {
      const result = await this.performUpload(queueItem);

      // Success
      queueItem.status = "success";
      queueItem.result = result;
      queueItem.onSuccess?.(result);
    } catch (error) {
      // Handle error with retry logic
      await this.handleUploadError(queueItem, error);
    } finally {
      // Cleanup
      this.activeUploads.delete(queueItem.id);
      queueItem.abortController = undefined;

      // Process next items in queue
      this.processUploadQueue();
    }
  }

  /**
   * Perform actual upload to server
   * @param queueItem Upload queue item
   * @returns Upload response
   */
  private async performUpload(
    queueItem: UploadQueueItem,
  ): Promise<TempImageUploadResponse> {
    const formData = new FormData();
    formData.append("image", queueItem.file);

    // Try to get JWT token (may not be available if using HTTP-only cookies)
    const token = this.getAuthToken();

    const headers: Record<string, string> = {};

    // Add authorization header if token is available
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(this.baseUrl, {
      method: "POST",
      headers,
      body: formData,
      credentials: "include", // Include HTTP-only cookies
      signal: queueItem.abortController?.signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Upload failed with status ${response.status}`,
      );
    }

    const result: TempImageUploadResponse = await response.json();
    return result;
  }

  /**
   * Handle upload error with exponential backoff retry
   * @param queueItem Upload queue item
   * @param error Error that occurred
   */
  private async handleUploadError(
    queueItem: UploadQueueItem,
    error: any,
  ): Promise<void> {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";

    // Check if this was an abort (user cancelled)
    if (error.name === "AbortError") {
      queueItem.status = "error";
      queueItem.error = "Upload cancelled";
      return;
    }

    queueItem.retryCount++;

    // Check if we should retry
    if (queueItem.retryCount <= queueItem.maxRetries) {
      // Exponential backoff: 1s, 2s, 4s, 8s...
      const delay = Math.pow(2, queueItem.retryCount - 1) * 1000;

      queueItem.status = "pending";
      queueItem.error = `Retry ${queueItem.retryCount}/${queueItem.maxRetries} in ${delay / 1000}s...`;

      // Wait and retry
      setTimeout(() => {
        if (this.uploadQueue.has(queueItem.id)) {
          this.processUploadQueue();
        }
      }, delay);
    } else {
      // Max retries reached
      queueItem.status = "error";
      queueItem.error = errorMessage;
      queueItem.onError?.(errorMessage);
    }
  }

  /**
   * Validate if file is a valid image
   * @param file File to validate
   * @returns True if valid image
   */
  private isValidImageFile(file: File): boolean {
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    return validTypes.includes(file.type);
  }

  /**
   * Generate unique upload ID
   * @returns Unique ID string
   */
  private generateUploadId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get authentication token from storage
   * @returns JWT token or null
   */
  private getAuthToken(): string | null {
    if (typeof window !== "undefined") {
      // Try to get from cookies (main method for this app)
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "access_token") {
          return decodeURIComponent(value);
        }
      }

      // Fallback to localStorage/sessionStorage (for development)
      const token =
        localStorage.getItem("auth_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("auth_token") ||
        sessionStorage.getItem("token") ||
        sessionStorage.getItem("access_token");

      if (token) {
        return token;
      }
    }

    return null;
  }

  /**
   * Queue an array of image URLs (temporary or external) for background processing
   * @param urls Array of image URLs (temp URLs from upload or remote images)
   * @param options Additional data such as propertyId or targetFolderType
   * @returns jobId returned by backend or null on error
   */
  async queueProcessing(
    urls: string[],
    options: {
      propertyId?: string;
      targetFolderType?: string;
    } = {},
  ): Promise<string | null> {
    if (!urls.length) return null;

    try {
      const token = this.getAuthToken();

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(this.queueEndpoint, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          tempImageUrls: urls,
          propertyId: options.propertyId,
          targetFolderType: options.targetFolderType,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        console.error("queueProcessing error", data);
        return null;
      }

      const data = await response.json();
      return data.jobId as string;
    } catch (err) {
      console.error("queueProcessing exception", err);
      return null;
    }
  }
}

// Export singleton instance
export const imageUploadService = new ImageUploadService();
